/**
 * Asking the server for a range of one stack's rows.
 *
 * A board pages **per stack**: `select_option=<id>,<limit>,<offset>` asks for a
 * slice of one column, and the answer is keyed by option id. So there is one
 * fetcher per stack, closing over which stack it is -- the same shape the public
 * grid's fetcher takes, where the thing closed over is a slug.
 *
 * One request per stack rather than one carrying all of them. The endpoint would
 * take several `select_option` parameters at once, and a coalescing layer could
 * turn N first loads into one -- but every window aborts and re-asks on its own
 * scroll, which is what lets one column be scrolled without disturbing the
 * others, and that independence is the whole reason a board is N windows. The
 * batching belongs on top of that if it is ever wanted, not instead of it.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData } from '@/api/types'
import type { RowFetcher } from '../rows/rowFetcher'

/** The shape the board endpoint answers with, narrowed to what is read. */
interface BoardRowsResponse {
  rows?: Record<string, { count?: number | null; results?: unknown[] } | undefined>
}

/** The key the server stacks a row under: the option's id, or the string `null`. */
export const stackKey = (optionId: number | null): string =>
  optionId === null ? 'null' : String(optionId)

function fetchFromApi(optionId: number | null): RowFetcher {
  return async (query, range, signal) => {
    const limit = Math.max(0, range.end - range.start)
    const body = await unwrap<BoardRowsResponse>(
      getApi().GET('/api/database/views/board/{view_id}/', {
        params: {
          path: { view_id: Number(query.viewId) },
          query: {
            // `id,limit,offset`, which is how this endpoint says "this much of
            // that column". Sent as the only `select_option`, so the answer
            // carries this stack and no other.
            select_option: `${stackKey(optionId)},${limit},${range.start}`,
            include: 'row_metadata',
            ...(query.search.term === ''
              ? {}
              : { search: query.search.term, search_mode: query.search.mode }),
          },
        },
        signal,
      }),
    )

    const stack = body.rows?.[stackKey(optionId)]
    return {
      rows: (stack?.results ?? []) as RowData[],
      // A stack the answer did not mention is not a stack with no rows: it is a
      // question that was not answered, and adopting a zero would empty a column
      // that has rows in it. `?? undefined` is what says so -- and it says it
      // for a stack that is missing and for one whose count is null alike, which
      // is why there is no separate check for the first. Written as one, an
      // explicit `stack === undefined` branch was the same expression twice and
      // no test could tell the two apart.
      count: stack?.count ?? undefined,
    }
  }
}

/**
 * The fetcher every board window uses, swappable for a stub.
 *
 * The same seam as `__setRowFetcherForTests` and for the same reason: under
 * jsdom a real request carrying an abort signal cannot be built at all.
 */
let factory: (optionId: number | null) => RowFetcher = fetchFromApi

export const boardRowFetcher = (optionId: number | null): RowFetcher => {
  const fetcher = factory(optionId)
  return (query, range, signal) => fetcher(query, range, signal)
}

/** Test seam: hand the board windows a fetcher factory, or `null` to restore the real one. */
export function __setBoardRowFetcherForTests(
  replacement: ((optionId: number | null) => RowFetcher) | null,
): void {
  factory = replacement ?? fetchFromApi
}
